import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SliderTile, { SliderTileItem } from 'Client/components/Generic/ContentInfusionsSlider/SliderTile';
import useBellaTranslator from 'Client/hooks/useTranslator';
import ContentInfusionsSlider, {
    contentfulFormatter,
    ImageHeights,
} from 'Client/components/Generic/ContentInfusionsSlider';
import { looksSliderRatios, looksSliderLandscapeRatios } from 'Client/constants/aspectRatios';
import LastSlideCard from 'Client/components/Generic/ContentInfusionsSlider/LastSlideCard';
import useFetchingAction from 'Client/hooks/useFetchingAction/useFetchingAction';
import { fetchLooksSliderIfNeeded } from 'Client/redux/looksSlider/actions';
import StateInterface from 'Client/redux/types';
import { LookSliderItem } from 'Client/redux/looksSlider/types';
import { useLooksTracking } from 'Client/components/Looks/hooks/useLooksTracking';
import { TrackLooksActions } from 'Common/ga/types';
import { setLDPKnownInfoAction } from 'Client/redux/lookDetail/actions';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';

interface OtherLooksInfusionsProps {
    lookSlug: string;
}

const OtherLooksInfusions = ({ lookSlug }: OtherLooksInfusionsProps) => {
    const t = useBellaTranslator();
    const dispatch = useDispatch();
    const { LDP } = useLooksTracking();
    const { p100IncreasedDesktopPageWidth } = useFeatureToggle();
    useFetchingAction(fetchLooksSliderIfNeeded(true, lookSlug), [lookSlug]);
    const looksData = useSelector<StateInterface, LookSliderItem[]>(
        state => state.lookDetailPage.looksSlider.looks || []
    );

    const onCardClick = (index: number, look: LookSliderItem) => {
        dispatch(setLDPKnownInfoAction(look));
        LDP.track(TrackLooksActions.LDPSliderClick, `Position: ${index + 1}, Look: ${look.trackingName}`);
    };

    const mapToCards = (look: LookSliderItem, index: number): SliderTileItem => {
        const details = {
            targetUrl: look.url,
            fallbackSrc: look.imgUrl,
            desktopSrc: contentfulFormatter(look.imgUrl, ImageHeights.LOOKS_DESKTOP),
            mobileSrc: contentfulFormatter(look.imgUrl, ImageHeights.LOOKS_MOBILE),
            title: look.title,
            ctaText: t('Read and Shop'),
            onClick: () => onCardClick(index, look),
            isLandscape: false,
        };

        if (look.isLandscape && look.moodImgUrl) {
            details.fallbackSrc = look.moodImgUrl;
            details.desktopSrc = contentfulFormatter(look.moodImgUrl, ImageHeights.LOOKS_DESKTOP);
            details.mobileSrc = contentfulFormatter(look.moodImgUrl, ImageHeights.LOOKS_MOBILE);
            details.isLandscape = true;
        }

        return details;
    };

    const cards = looksData.map(mapToCards);

    if (cards.length === 0) {
        return null;
    }

    return (
        <ContentInfusionsSlider
            title={t('You may also like')}
            ctaOptions={{ link: '/looks/', text: t('See all') }}
            data-testid="content-infusions-slider"
        >
            {cards.map(look => (
                <SliderTile
                    ratio={look.isLandscape ? looksSliderLandscapeRatios : looksSliderRatios}
                    card={look}
                    key={look.title}
                    p100IncreasedDesktopPageWidth={p100IncreasedDesktopPageWidth}
                />
            ))}
            <LastSlideCard
                title={t('Shop the Look')}
                ctaText={t('See all')}
                link="/looks/"
                ratio={looksSliderRatios}
                data-testid="content-infusions-slider-last-card"
            />
        </ContentInfusionsSlider>
    );
};

export default OtherLooksInfusions;
